import { BlockchainID, IWhitelistBlockchainsParams } from 'multirpc-sdk';

import { MultiService } from 'modules/api/MultiService';
import { RootState } from 'store';
import { createNotifyingQueryFn } from 'store/utils/createNotifyingQueryFn';
import { createQuerySelectors } from 'store/utils/createQuerySelectors';
import { hasAllBlockchainsInWhitelist } from 'modules/projects/utils/hasAllBlockchainsInWhitelist';
import { selectAllChainsPaths } from 'modules/chains/store/selectors';
import { selectJWTs } from 'domains/jwtToken/action/fetchJWTs';
import { web3Api } from 'store/queries';
import { mapSharkChainToChainPath } from 'modules/chains/utils/mapSharkChainNameToChainPath';

import { IProjectWithBlockchains } from '../types';
import { fetchProjectsWhitelistsBlockchains } from './fetchProjectsWhitelistsBlockchains';

export interface IFetchProjectWhitelistBlockchainsParams
  extends IWhitelistBlockchainsParams {}

// The endpoint name is listed in endpointsSerializedByParams constant
// in packages/protocol/src/store/queries/index.ts file.
// If the name has changed it should be reflected there as well.
export const {
  endpoints: { fetchProjectWhitelistBlockchains },
  useFetchProjectWhitelistBlockchainsQuery,
  useLazyFetchProjectWhitelistBlockchainsQuery,
} = web3Api.injectEndpoints({
  endpoints: build => ({
    fetchProjectWhitelistBlockchains: build.query<
      BlockchainID[],
      IFetchProjectWhitelistBlockchainsParams
    >({
      queryFn: createNotifyingQueryFn(async (params, { getState }) => {
        const api = MultiService.getService().getAccountingGateway();

        const data = await api.getWhitelistBlockchains(params);

        // empty array of blockchains means all chains are added to project
        if (hasAllBlockchainsInWhitelist(data)) {
          const allBlockchainsPaths = selectAllChainsPaths(
            getState() as RootState,
          );

          return { data: allBlockchainsPaths };
        }

        return {
          data: data.map(chain => mapSharkChainToChainPath(chain)),
        };
      }),
      onQueryStarted: async (
        { group, token },
        { dispatch, getState, queryFulfilled },
      ) => {
        const { data } = await queryFulfilled;
        const state = getState() as RootState;

        const projects = selectJWTs(state, { group });

        dispatch(
          web3Api.util.updateQueryData(
            fetchProjectsWhitelistsBlockchains.name as never,
            { group, projects } as never,
            (draft: IProjectWithBlockchains[] = []) => {
              const project = draft.find(
                ({ userEndpointToken }) => userEndpointToken === token,
              );

              if (project) {
                project.blockchains = data;
              }
            },
          ),
        );
      },
    }),
  }),
  overrideExisting: true,
});

export const fetchProjectWhitelistBlockchainsFallback: BlockchainID[] = [];

export const {
  selectDataWithFallbackCachedByParams: selectProjectWhitelistBlockchains,
  selectLoadingCachedByParams: selectProjectWhitelistBlockchainsLoading,
  selectStateCachedByParams: selectProjectWhitelistBlockchainsState,
} = createQuerySelectors({
  endpoint: fetchProjectWhitelistBlockchains,
  fallback: fetchProjectWhitelistBlockchainsFallback,
});
